/*----------------------------------
- HOME SOCIAL PROOF
----------------------------------*/

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { AvatarGroup, type AvatarGroupItem } from '@uniquedomains/components/ui/avatar-group';
import Icon from '@/client/components/ui/icon/Icon';
import communityMember1 from '@/client/assets/img/community-avatars/community-member-1.webp';
import communityMember2 from '@/client/assets/img/community-avatars/community-member-2.webp';
import communityMember3 from '@/client/assets/img/community-avatars/community-member-3.webp';
import communityMember4 from '@/client/assets/img/community-avatars/community-member-4.webp';
import communityMember5 from '@/client/assets/img/community-avatars/community-member-5.webp';
import { HOME_SOCIAL_PROOF_COPY } from '@/client/catalogs/landing/pageContent';

/*----------------------------------
- TYPES
----------------------------------*/

/** `hero` sits on the light page background, `finalCta` inside the dark closing card. */
type HomeSocialProofSurface = 'hero' | 'finalCta';

type HomeSocialProofProps = {
    totalSignups: number;
    surface: HomeSocialProofSurface;
};

type SurfacePresentation = {
    testId: string;
    /** The final CTA shows a shorter strip so it does not compete with the two persona cards under it. */
    visibleAvatars: number;
    rowClass: string;
    groupClass: string;
    avatarClass: string;
    fallbackClass: string;
    sentenceClass: string;
    countClass: string;
    /** Bolded lead-in. The hero emphasizes the number alone, the final CTA emphasizes "+N people". */
    emphasis: (formattedCount: string) => string;
    suffix: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SIGNUP_COUNT_FORMATTER = new Intl.NumberFormat('en-US');

const SOCIAL_PROOF_AVATARS: readonly Omit<AvatarGroupItem, 'fallback'>[] = [
    { id: 'community-member-1', src: communityMember1 },
    { id: 'community-member-2', src: communityMember2 },
    { id: 'community-member-3', src: communityMember3 },
    { id: 'community-member-4', src: communityMember4 },
    { id: 'community-member-5', src: communityMember5 },
];

const SURFACE_PRESENTATION: Record<HomeSocialProofSurface, SurfacePresentation> = {
    hero: {
        testId: 'landing-hero-social-proof',
        visibleAvatars: SOCIAL_PROOF_AVATARS.length,
        // Beside the strip on a phone, under the centred hero from `lg`.
        rowClass: 'mt-5 flex items-center gap-3 lg:mt-6 lg:justify-center lg:gap-4',
        groupClass: '-space-x-3 overflow-visible',
        avatarClass: 'h-9 w-9 border-[3px] bg-slate-100 shadow-none lg:h-10 lg:w-10',
        fallbackClass: 'h-4 w-4 text-slate-500',
        sentenceClass: 'text-pretty text-[14.5px] font-medium leading-[1.4] text-slate-600 lg:text-base lg:leading-6',
        countClass: 'font-semibold text-slate-700',
        emphasis: (formattedCount) => `+${formattedCount}`,
        suffix: HOME_SOCIAL_PROOF_COPY.hero,
    },
    finalCta: {
        testId: 'landing-final-cta-social-proof',
        visibleAvatars: 3,
        rowClass: 'mt-5 flex flex-wrap items-center gap-3 lg:mt-[22px] lg:justify-center',
        // Borders match the dark card rather than the page, so the strip reads as cut out of it.
        groupClass: '-space-x-[10px] overflow-visible',
        avatarClass: 'h-9 w-9 border-2 border-[#2a2b2c] bg-slate-800 shadow-none',
        fallbackClass: 'h-4 w-4 text-white/60',
        sentenceClass: 'text-pretty text-[13.5px] font-medium leading-[1.45] text-white/60 lg:leading-6',
        countClass: 'font-semibold text-white/85',
        emphasis: (formattedCount) => `+${formattedCount} people`,
        suffix: HOME_SOCIAL_PROOF_COPY.finalCta,
    },
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeSocialProof = ({ totalSignups, surface }: HomeSocialProofProps) => {
    if (totalSignups <= 0) return null;

    const presentation = SURFACE_PRESENTATION[surface];
    const avatars = SOCIAL_PROOF_AVATARS.slice(0, presentation.visibleAvatars).map((avatar) => ({
        ...avatar,
        fallback: <Icon name="user-round" size={24} class={presentation.fallbackClass} />,
    }));

    return (
        <div data-testid={presentation.testId} class={presentation.rowClass}>
            <AvatarGroup
                avatars={avatars}
                aria-label="Unique Domains community members"
                class={presentation.groupClass}
                avatarClass={presentation.avatarClass}
            />
            <p class={presentation.sentenceClass}>
                <span class={presentation.countClass}>
                    {presentation.emphasis(SIGNUP_COUNT_FORMATTER.format(totalSignups))}
                </span>{' '}
                <span>{presentation.suffix}</span>
            </p>
        </div>
    );
};

export default HomeSocialProof;
